import React, { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils2";

export default function Settings() {
  const { user, setUser } = useAuth();
  const [settings, setSettings] = useState(user?.settings || {});
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", settings);
      setUser(data);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 sm:p-8 lg:p-10 max-w-3xl mx-auto">
      <div className="text-[10px] uppercase tracking-[0.3em] text-orange-500 mb-2">// Operator profile</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mb-2">Settings</h1>
      <p className="text-zinc-400 mb-8">Reminder cadence and defaults.</p>

      <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 space-y-6">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Account</div>
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-sm bg-zinc-800 grid place-items-center overflow-hidden">
              {user?.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : <span className="font-display font-bold text-orange-500">{(user?.name || "?").slice(0, 1).toUpperCase()}</span>}
            </div>
            <div>
              <div className="font-display font-bold text-lg">{user?.name}</div>
              <div className="text-xs text-zinc-500 font-mono">{user?.email} · via {user?.provider}</div>
            </div>
          </div>
        </div>

        <div className="border-t border-zinc-800 pt-6">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Reminders</div>
          <div className="flex items-center justify-between py-3">
            <div>
              <Label className="text-sm">Enable reminders</Label>
              <div className="text-xs text-zinc-500">Show in-app notifications for upcoming maintenance.</div>
            </div>
            <Switch
              checked={settings.reminders_enabled ?? true}
              onCheckedChange={(v) => setSettings({ ...settings, reminders_enabled: v })}
              data-testid="reminders-enabled"
            />
          </div>
          <div className="flex items-center justify-between py-3 border-t border-zinc-800">
            <div>
              <Label className="text-sm">Reminder cadence</Label>
              <div className="text-xs text-zinc-500">How often the app checks for due tasks.</div>
            </div>
            <Select value={settings.reminder_cadence || "weekly"} onValueChange={(v) => setSettings({ ...settings, reminder_cadence: v })}>
              <SelectTrigger className="w-36 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="reminder-cadence"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly (weekends)</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between py-3 border-t border-zinc-800">
            <div>
              <Label className="text-sm">Default category for new equipment</Label>
              <div className="text-xs text-zinc-500">Pre-selected when adding gear.</div>
            </div>
            <Select value={settings.default_category || "Home"} onValueChange={(v) => setSettings({ ...settings, default_category: v })}>
              <SelectTrigger className="w-28 bg-zinc-950 border-zinc-800 rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="Home">Home</SelectItem>
                <SelectItem value="Work">Work</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="border-t border-zinc-800 pt-6">
          <Button onClick={save} disabled={saving} className="bg-orange-600 hover:bg-orange-500 rounded-sm uppercase tracking-widest text-xs font-bold" data-testid="save-settings">
            {saving ? "Saving..." : "Save settings"}
          </Button>
        </div>
      </div>
    </div>
  );
}
